import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, KeyRound, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  useIntegrationOverview,
  useTriggerSync,
  useUpdateCredential,
  useUpdateCredentialSet,
  useRemoveIntegration,
} from '@/hooks/useIntegrations'
import { OverallHealthBadge } from '@/components/integrations/OverallHealthBadge'
import { ProviderIcon } from '@/components/integrations/ProviderIcon'
import { ProviderDashboard } from '@/components/integrations/ProviderDashboard'
import { ProviderLocationCard } from '@/components/integrations/ProviderLocationCard'
import { SyncLogViewer } from '@/components/integrations/SyncLogViewer'
import { CredentialRotationDialog } from '@/components/integrations/CredentialRotationDialog'
import { RemoveIntegrationDialog } from '@/components/integrations/RemoveIntegrationDialog'
import type { CredentialModel } from '@/types/integrations'

export function IntegrationDetailPage() {
  const { credentialSetId } = useParams<{ credentialSetId: string }>()
  const navigate = useNavigate()

  const [rotateOpen, setRotateOpen] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set())
  const [togglingCredentialId, setTogglingCredentialId] = useState<string | null>(null)

  const { data: overviewData, isLoading } = useIntegrationOverview()
  const triggerSync = useTriggerSync()
  const updateCredential = useUpdateCredential()
  const updateCredentialSet = useUpdateCredentialSet()
  const removeIntegration = useRemoveIntegration()

  // Find this specific credential set from the overview data
  const overview = overviewData?.find(o => o.id === credentialSetId)

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!overview) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/settings/integrations')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Integrations
        </Button>
        <p className="text-muted-foreground">Integration not found.</p>
      </div>
    )
  }

  const provider = overview.integration_providers
  const providerName = provider?.name ?? overview.provider
  const providerType = provider?.provider_type ?? 'reservation'
  const credentialModel = provider?.credential_model ?? 'per_org'
  const credentials = overview.integration_credentials
  const credentialIds = credentials.map(c => c.id)

  const handleMasterToggle = (isActive: boolean) => {
    updateCredentialSet.mutate({ id: overview.id, isActive })
  }

  const handleLocationToggle = (credentialId: string, isActive: boolean) => {
    setTogglingCredentialId(credentialId)
    updateCredential.mutate(
      { id: credentialId, updates: { is_active: isActive } },
      { onSettled: () => setTogglingCredentialId(null) },
    )
  }

  const handleSync = (credentialId: string) => {
    setSyncingIds(prev => new Set(prev).add(credentialId))
    triggerSync.mutate(credentialId, {
      onSettled: () => {
        setSyncingIds(prev => {
          const next = new Set(prev)
          next.delete(credentialId)
          return next
        })
      },
    })
  }

  const handleRemoveConfirm = () => {
    removeIntegration.mutate(
      { credentialSetId: overview.id, credentialModel: credentialModel as CredentialModel },
      {
        onSuccess: () => {
          setRemoveOpen(false)
          navigate('/settings/integrations')
        },
      },
    )
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/settings/integrations')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Integrations
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ProviderIcon providerType={providerType} className="h-6 w-6 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{providerName}</h1>
            <p className="text-sm text-muted-foreground">{overview.label}</p>
          </div>
          <OverallHealthBadge credentials={credentials} />
        </div>
        <Switch
          checked={overview.is_active}
          onCheckedChange={handleMasterToggle}
          disabled={updateCredentialSet.isPending}
        />
      </div>

      {/* Dashboard */}
      <ProviderDashboard credentials={credentials} />

      {/* Tabs */}
      <Tabs defaultValue="locations">
        <TabsList>
          <TabsTrigger value="locations">Locations</TabsTrigger>
          <TabsTrigger value="log">Sync Log</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="locations" className="mt-4 space-y-3">
          {credentials.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No locations mapped to this integration yet.
            </p>
          ) : (
            credentials.map(cred => (
              <ProviderLocationCard
                key={cred.id}
                credential={cred}
                masterActive={overview.is_active}
                onToggle={handleLocationToggle}
                onSync={handleSync}
                isToggling={togglingCredentialId === cred.id}
                isSyncing={syncingIds.has(cred.id)}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="log" className="mt-4">
          <SyncLogViewer credentialIds={credentialIds} />
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2">Credential Management</h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRotateOpen(true)}
                >
                  <KeyRound className="h-3 w-3 mr-1.5" />
                  Rotate Credentials
                </Button>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-sm font-medium mb-2 text-destructive">Danger Zone</h3>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={() => setRemoveOpen(true)}
              >
                <Trash2 className="h-3 w-3 mr-1.5" />
                Remove Integration
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <CredentialRotationDialog
        open={rotateOpen}
        onOpenChange={setRotateOpen}
        credentialSetId={overview.id}
        credentialLabel={overview.label}
      />

      <RemoveIntegrationDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        providerName={providerName}
        credentialModel={credentialModel}
        onConfirm={handleRemoveConfirm}
        isPending={removeIntegration.isPending}
      />
    </div>
  )
}
